  "use client"

  import { useState, useRef } from "react"
  import { X, Loader, Youtube, Sparkles, ExternalLink } from "lucide-react"
  import { postAws } from "../postAws"

  export const YouTubeCourseModal = ({ isOpen, onClose, onSubmit, isProcessing }) => {
    const [formData, setFormData] = useState({
      title: "",
      description: "",
      playlistUrl: "",
      thumbnail: null,
    })
    const [errors, setErrors] = useState({})
    const [isGeneratingDescription, setIsGeneratingDescription] = useState(false)
    const [isFetchingPlaylist, setIsFetchingPlaylist] = useState(false)
    const [playlistData, setPlaylistData] = useState(null)
    const [playlistError, setPlaylistError] = useState("")

    const fileInputRef = useRef(null)

    const handleInputChange = (e) => {
      const { name, value } = e.target
      setFormData((prev) => ({ ...prev, [name]: value }))
      // Clear error when user starts typing
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }))
      }

      // Clear playlist data when URL changes
      if (name === "playlistUrl") {
        setPlaylistData(null)
        setPlaylistError("")
      }
    }

    const handleFileChange = (e) => {
      const file = e.target.files[0]
      if (file) {
        setFormData((prev) => ({ ...prev, thumbnail: file }))
      }
    }

    const fetchPlaylistData = async () => {
      if (!formData.playlistUrl.trim()) {
        setErrors((prev) => ({ ...prev, playlistUrl: "Please enter a YouTube playlist URL first" }))
        return
      }

      if (
        !formData.playlistUrl.includes("youtube.com/playlist?list=") &&
        !formData.playlistUrl.includes("youtu.be/playlist?list=")
      ) {
        setErrors((prev) => ({ ...prev, playlistUrl: "Please enter a valid YouTube playlist URL" }))
        return
      }

      setIsFetchingPlaylist(true)
      setPlaylistError("")

      try {
        const playlistResponse = await postAws("https://api-dev.aurait.ai/playlistFromYoutube", "POST", {
          playlist_url: formData.playlistUrl,
        })

        if (playlistResponse.statusCode !== 200) {
          throw new Error("Failed to fetch playlist data")
        }

        const playlistData = JSON.parse(playlistResponse.body)
        setPlaylistData(playlistData)

        // Auto-fill title if not already set
        if (!formData.title && playlistData.title) {
          setFormData((prev) => ({ ...prev, title: playlistData.title }))
        }
      } catch (error) {
        console.error("Error fetching playlist:", error)
        setPlaylistError("Failed to fetch playlist data. Please check the URL and try again.")
      } finally {
        setIsFetchingPlaylist(false)
      }
    }

    const generateAIDescription = async () => {
      if (!formData.title.trim()) {
        setErrors((prev) => ({ ...prev, title: "Please enter a course title first" }))
        return
      }

      if (!playlistData) {
        setPlaylistError("Please fetch playlist data first by clicking 'Fetch Playlist' button")
        return
      }

      setIsGeneratingDescription(true)
      try {
        // Extract video titles from playlist data
        const videoTitles = playlistData.videos
          .filter((video) => video.title !== "Private video" && video.title)
          .map((video) => video.title)

        const payload = {
          request: "suggest-description-playlist",
          body: {
            topicTitle: formData.title,
            videoTitles: videoTitles,
          },
        }

        const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", payload)

        if (response.statusCode === 200) {
          const responseData = JSON.parse(response.body)
          const description = responseData.description

          let formattedDescription = `${description.intro}\n\n`

          if (description.whatYouWillLearn && description.whatYouWillLearn.length > 0) {
            formattedDescription += "What you'll learn:\n"
            description.whatYouWillLearn.forEach((item) => {
              formattedDescription += `• ${item}\n`
            })
            formattedDescription += "\n"
          }

          if (description.outcomes && description.outcomes.length > 0) {
            formattedDescription += "Course outcomes:\n"
            description.outcomes.forEach((item) => {
              formattedDescription += `• ${item}\n`
            })
          }

          setFormData((prev) => ({ ...prev, description: formattedDescription }))
        }
      } catch (error) {
        console.error("Error generating description:", error)
        setPlaylistError("Failed to generate description. Please try again.")
      } finally {
        setIsGeneratingDescription(false)
      }
    }

    const validateForm = () => {
      const newErrors = {}

      if (!formData.title.trim()) {
        newErrors.title = "Course title is required"
      }

      if (!formData.description.trim()) {
        newErrors.description = "Course description is required"
      }

      if (!formData.playlistUrl.trim()) {
        newErrors.playlistUrl = "YouTube playlist URL is required"
      } else if (
        !formData.playlistUrl.includes("youtube.com/playlist?list=") &&
        !formData.playlistUrl.includes("youtu.be/playlist?list=")
      ) {
        newErrors.playlistUrl = "Please enter a valid YouTube playlist URL"
      }

      if (!playlistData) {
        newErrors.playlistUrl = "Please fetch playlist data first"
      }

      setErrors(newErrors)
      return Object.keys(newErrors).length === 0
    }

    const handleSubmit = (e) => {
      e.preventDefault()
      if (validateForm()) {
        onSubmit(formData)
      }
    }

    const handleClose = () => {
      setFormData({ title: "", description: "", playlistUrl: "", thumbnail: null })
      setErrors({})
      setPlaylistData(null)
      setPlaylistError("")
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
      onClose()
    }

    if (!isOpen) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <Youtube className="h-6 w-6 mr-2 text-red-600" />
              Create Course from YouTube Playlist
            </h2>
            <button
              onClick={handleClose}
              disabled={isProcessing}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* YouTube Playlist URL Section - Now at the top */}
            <div>
              <label htmlFor="playlistUrl" className="block text-sm font-medium text-gray-700 mb-1">
                YouTube Playlist URL *
              </label>
              <div className="flex gap-2">
                <input
                  id="playlistUrl"
                  name="playlistUrl"
                  type="url"
                  value={formData.playlistUrl}
                  onChange={handleInputChange}
                  placeholder="https://www.youtube.com/playlist?list=PLAYLIST_ID"
                  className={`flex-1 p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.playlistUrl ? "border-red-500" : "border-gray-300"
                  }`}
                  disabled={isProcessing}
                />
                <button
                  type="button"
                  onClick={fetchPlaylistData}
                  disabled={isFetchingPlaylist || isProcessing || !formData.playlistUrl.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center disabled:bg-blue-400"
                >
                  {isFetchingPlaylist ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin mr-1" />
                      Fetching...
                    </>
                  ) : (
                    "Fetch Playlist"
                  )}
                </button>
              </div>
              {errors.playlistUrl && <p className="mt-1 text-sm text-red-600">{errors.playlistUrl}</p>}
              {playlistError && <p className="mt-1 text-sm text-red-600">{playlistError}</p>}

              {/* Display playlist info when fetched */}
              {playlistData && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Playlist: {playlistData.title || "YouTube Playlist"}
                      </p>
                      <p className="text-sm text-green-600">{playlistData.videos?.length || 0} videos found</p>
                    </div>
                    <a
                      href={formData.playlistUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-800"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Course Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Course Title *
              </label>
              <input
                id="title"
                name="title"
                type="text"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter course title"
                className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.title ? "border-red-500" : "border-gray-300"
                }`}
                disabled={isProcessing}
              />
              {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
            </div>

            {/* Course Description with AI Generation */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Course Description *
              </label>
              <div className="relative">
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter course description or generate with AI"
                  rows={6}
                  className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.description ? "border-red-500" : "border-gray-300"
                  }`}
                  disabled={isProcessing}
                />
                <button
                  type="button"
                  onClick={generateAIDescription}
                  disabled={isGeneratingDescription || isProcessing || !playlistData}
                  className="absolute right-2 bottom-2 px-3 py-1 bg-purple-600 text-white text-xs rounded-md hover:bg-purple-700 transition-colors flex items-center disabled:bg-purple-400"
                >
                  {isGeneratingDescription ? (
                    <>
                      <Loader className="w-3 h-3 animate-spin mr-1" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3 h-3 mr-1" />
                      Ask AI
                    </>
                  )}
                </button>
              </div>
              {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
              {!playlistData && (
                <p className="mt-1 text-sm text-amber-600">
                  Fetch playlist data first to enable AI description generation
                </p>
              )}
            </div>

            {/* Thumbnail Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course Thumbnail (Optional)</label>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing}
                className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formData.thumbnail ? (
                  <div className="flex items-center justify-center">
                    <span className="text-green-600">✓ {formData.thumbnail.name}</span>
                  </div>
                ) : (
                  "Click to upload thumbnail image"
                )}
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
                disabled={isProcessing}
              />
              <p className="mt-1 text-xs text-gray-500">Recommended: 16:9 aspect ratio, max 5MB</p>
            </div>

            <div className="bg-blue-50 p-4 rounded-md">
              <h4 className="font-medium text-blue-900 mb-2">How it works:</h4>
              <ol className="text-sm text-blue-800 space-y-1">
                <li>1. Enter YouTube playlist URL and fetch playlist data</li>
                <li>2. Set course title and generate AI description based on video content</li>
                <li>3. We'll create your course and generate structure with AI-powered topics</li>
                <li>4. Match videos to the generated course structure automatically</li>
              </ol>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                disabled={isProcessing}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isProcessing || !playlistData}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center disabled:bg-blue-400"
              >
                {isProcessing ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Youtube className="h-4 w-4 mr-2" />
                    Create Course
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }
